/** How long the fade in `_layout.scss` runs; the node is removed once it has finished. */
const FADE_MS = 200;

/**
 * Takes down the boot shell — the chrome index.html paints before Angular exists (see the comment
 * on `#boot-shell` there). Called on the first settled navigation, which is the moment the routed
 * layout it stands in for is on the page.
 *
 * Idempotent, and safe to call when the shell is already gone: index.html removes it outright on
 * the bare-layout routes, and again on a failsafe timer.
 */
export function dismissBootShell(): void {
  const shell = document.getElementById('boot-shell');
  if (!shell) {
    return;
  }
  shell.classList.add('boot-shell--done');
  setTimeout(() => shell.remove(), FADE_MS);
}

/**
 * Puts the resolved brand logo into the space the boot shell holds open for it. The mark differs by
 * environment and by whitelabel, so index.html — one file for all of them — cannot name it; this
 * runs as soon as CustomUiService has settled the theme, which is well before the routed layout
 * exists. A no-op once the shell is gone.
 */
export function paintBootShellBrand(logoUrl: string): void {
  const logo = document.querySelector<HTMLElement>('#boot-shell .boot-shell__logo');
  if (!logo || !logoUrl) {
    return;
  }
  // A background image, not an `<img>`: the sidebar paints its own mark this way, and the two
  // fitting algorithms round the drawn size differently enough to shift it at the handover.
  logo.style.backgroundImage = `url(${logoUrl})`;
}

/**
 * Drops the shell's stand-in footer and lets the real one show through. The footer is rendered by
 * the app shell itself, so its text — version, year, the credit — is on the page from Angular's
 * first paint, long before the routed layout. The band exists only for the frames before that,
 * where there is no footer at all to leave a gap for.
 */
export function revealAppFooter(): void {
  const shell = document.getElementById('boot-shell');
  if (!shell) {
    return;
  }
  shell.querySelector('.boot-shell__footer')?.remove();
  shell.classList.add('boot-shell--footer-live');
}
